import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_BASE } from '@/utils/api';
import { StepBusiness, StepTables } from '@/components/onboarding/StepsBasic';
import { StepMenu } from '@/components/onboarding/StepMenu';
import { StepTeam, StepTest } from '@/components/onboarding/StepsFinal';

const STEPS = [
  ['business', 'Tu negocio', 'Nombre, rubro, logo y condición fiscal'],
  ['tables', 'Mesas', 'Creá todas tus mesas o sombrillas de una vez'],
  ['menu', 'Menú', 'A mano, con Excel o desde una foto de la carta'],
  ['team', 'Equipo', 'Invitá a tu personal por email'],
  ['test', 'Prueba', 'Escaneá un QR y hacé un pedido de prueba'],
];

const track = (step, event, seconds) => axios.post(`${API_BASE}/onboarding/track`, { step, event, seconds }).catch(() => {});

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [ob, setOb] = useState(null);
  const [idx, setIdx] = useState(0);
  const enteredAt = useRef(Date.now());

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/onboarding`);
    setOb(data);
    return data;
  }, []);

  useEffect(() => {
    load().then(d => {
      const wanted = STEPS.findIndex(s => s[0] === params.get('step'));
      const firstPending = d.checklist.findIndex(i => !i.done);
      setIdx(wanted >= 0 ? wanted : Math.max(0, firstPending));
      if (d.dismissed) axios.post(`${API_BASE}/onboarding/dismiss`, { dismissed: false }).catch(() => {});
    });
  }, [load, params]);

  const step = STEPS[idx][0];
  useEffect(() => {
    if (!ob) return undefined;
    enteredAt.current = Date.now();
    track(step, 'view');
    const leave = () => track(step, 'leave', (Date.now() - enteredAt.current) / 1000);
    window.addEventListener('beforeunload', leave);
    return () => { window.removeEventListener('beforeunload', leave); leave(); };
  }, [step, ob === null]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = async () => {
    const d = await load();
    if (idx < STEPS.length - 1) setIdx(idx + 1);
    else navigate('/dashboard');
    return d;
  };
  const skip = async () => { await track(step, 'skip'); next(); };
  const exit = async () => { await axios.post(`${API_BASE}/onboarding/dismiss`, { dismissed: true }); navigate('/dashboard'); };

  if (!ob) return <div className="min-h-screen grid place-items-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  const [, title, subtitle] = STEPS[idx];
  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-page">
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-14 grid md:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-1" data-testid="onboarding-steps-nav">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Alta de tu negocio</p>
          {STEPS.map(([k, label], i) => {
            const done = ob.checklist.find(c => c.step === k)?.done;
            return (
              <button key={k} onClick={() => setIdx(i)} data-testid={`onboarding-nav-${k}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm text-left transition-colors ${i === idx ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold ${done ? 'bg-green-600 text-white' : i === idx ? 'bg-white/20' : 'bg-muted'}`}>{done ? <Check className="w-3.5 h-3.5" /> : i + 1}</span>
                {label}
              </button>
            );
          })}
          <Button variant="ghost" className="w-full justify-start mt-4 text-muted-foreground" onClick={exit} data-testid="onboarding-exit-btn"><X className="w-4 h-4 mr-2" />Seguir después</Button>
        </aside>
        <main className="bg-card border border-border rounded-3xl p-6 md:p-10">
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Paso {idx + 1} de {STEPS.length}</p>
              <h1 className="font-heading text-3xl md:text-4xl font-bold" data-testid="onboarding-step-title">{title}</h1>
              <p className="text-muted-foreground mt-1">{subtitle}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={skip} data-testid="onboarding-skip-btn">Saltear</Button>
          </div>
          {step === 'business' && <StepBusiness initial={ob.business} onDone={next} />}
          {step === 'tables' && <StepTables businessType={ob.business?.business_type} onDone={next} />}
          {step === 'menu' && <StepMenu onDone={next} />}
          {step === 'team' && <StepTeam onDone={next} />}
          {step === 'test' && <StepTest onDone={next} />}
        </main>
      </div>
    </div>
  );
}
